/**
 * Typed fetch wrapper around the Cloudflare Worker API.
 *
 * Automatically attaches the Supabase access token as a Bearer header on
 * every request. If the user isn't signed in, the request still goes
 * through (worker returns 401 → caller's error handler shows the right UX).
 *
 * Base URL comes from VITE_API_URL or falls back to the production worker.
 */
import { supabase } from './supabase';
import { readCurrentScope } from './hooks/view-scope';
import { readImpersonationTarget } from './impersonation';
import { getDemoAccountId } from '@/lib/utils/demo-mode';
import { readAttributionHeader, clearAttribution } from './attribution';
import { readProductHeader, clearProduct } from './product';

const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : 'https://api.waypoint-intel.com');

/** Public accessor for the API base — used by EventSource clients that
 * can't go through the api() wrapper (EventSource doesn't allow headers). */
export function getApiBase(): string { return API_URL; }

/** Resolve a fresh Supabase access token. Returns null if the user is
 * signed out. Used by streaming clients that need to put the token in
 * a query param since EventSource doesn't support Authorization headers. */
export async function getAccessToken(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  // Pull the current Supabase session each call so we always send a fresh
  // (auto-refreshed) access token. supabase-js caches + auto-refreshes
  // in the background so this is cheap.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Waypoint-Scope': readCurrentScope(),
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  // If owner/admin is impersonating another user, send the header so the
  // server resolves auth.role + departments as the target. Skip for the
  // view-as/start endpoint itself (that's the OWNER's action to log it).
  const impersonate = readImpersonationTarget();
  if (impersonate && !path.startsWith('/v1/team/view-as')) {
    headers['X-Waypoint-Impersonate'] = impersonate.userId;
  }
  // Demo mode — switch the active account on every call (except the demo
  // enter/exit endpoints themselves, which act on the real account).
  const demoAccountId = getDemoAccountId();
  if (demoAccountId && !path.startsWith('/v1/demo/')) {
    headers['X-Waypoint-Account-Override'] = demoAccountId;
  }
  // First-touch signup attribution (see lib/attribution.ts). Present only until
  // a call succeeds, and only read server-side when this request is the one
  // that provisions the workspace — so it rides along on ~1-2 requests.
  const attribution = readAttributionHeader();
  // Product front door (lib/product.ts): tells the provisioning call which
  // product this brand-new workspace is for. Cleared once a call succeeds.
  const product = readProductHeader();
  if (product) headers['X-Waypoint-Product'] = product;
  if (attribution) {
    headers['X-Waypoint-Attribution'] = attribution;
  }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch {
    // No session — let the worker return 401; caller decides what to do.
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });

  // The server has now seen the attribution header (it only acts on it while
  // creating the account). Drop it so it stops riding along on later calls.
  // A 401 means we were never authenticated, so keep it for the retry.
  if (attribution && res.status !== 401) {
    clearAttribution();
  }
  if (product && res.ok) {
    clearProduct();
  }

  const text = await res.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* leave as string */
  }

  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed: ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}

// Common endpoints
export const apiHealth = () => api<{ status: string; timestamp: string }>('/health');
